/**
 * Regions: one feature for colouring, spotlighting and labelling parts of a scene. It replaces
 * Focus & Highlight and Annotation Layers, which were the same splat-recolouring engine with two
 * UIs; scenes saved with either convert on load (regionsFromLegacy) and look the same.
 *
 * A region is
 * - WHERE: one or more areas from the shared shape picker (outline, sphere, box, splat objects;
 *   inside or outside), see area-shape.ts;
 * - WHAT IT DOES: a colour inside it (`color` at `colorStrength`, 0 = no colour) and, optionally,
 *   dimming the rest of the scene while it is on (`dimOutside`: brightness and saturation of
 *   everything outside it). A region that only colours is what an annotation layer was; one that
 *   dims the rest is what a focus region was;
 * - WHEN IT SHOWS: on when the scene opens (`startsOn`), switched by triggers (waypoints,
 *   hotspots, time, the camera's area...), and/or by visitors in the Layers menu (`inLayersMenu`).
 *
 * Several regions dimming at once: the strongest dimming wins (lowest brightness, lowest
 * saturation of the regions that are on), and nothing inside any of them is dimmed.
 *
 * Every region fades in and out (no shader rebuild): regions are compiled into the shader once
 * and switched by uniforms, the way annotation layers always worked.
 *
 * COORDINATES follow area-shape.ts: sphere / box fields are Z-mirrored editor coordinates,
 * outline points runtime coordinates.
 */
import type { AreaApply, AreaShape, AreaShapeKind } from './area-shape';
import type { FocusConfig, FocusRegion } from './focus-regions';
import { DEFAULT_FOCUS_TINT, MAX_FOCUS_REGIONS } from './focus-regions';
import type { AnnotationLayerGroup, AnnotationLayersConfig, AnnotationShape } from './annotation-layers';
import { DEFAULT_ANNOTATION_COLORS, DEFAULT_ANNOTATION_OPACITY } from './annotation-layers';
import type { SceneTrigger } from '../types';

/** One area of a region: a shape from the shared picker. */
export interface RegionArea extends Partial<Omit<AreaShape, 'shape'>> {
    id: string;
    /** Optional label for the area (e.g. "North façade"). */
    name?: string;
    shape: AreaShapeKind;
    /** False keeps the area in the file without drawing it. Default true. */
    enabled?: boolean;
}

/** How the rest of the scene looks while a region that dims it is on. */
export interface RegionDim {
    /** Brightness of everything outside, 0 (black) to 1 (unchanged). */
    brightness: number;
    /** Colour saturation of everything outside, 0 (grey) to 1 (unchanged). */
    saturation: number;
}

export interface SceneRegion {
    id: string;
    name: string;
    /** False keeps the region in the file without drawing it. */
    enabled: boolean;
    areas: RegionArea[];
    /** Colour inside the region, #rrggbb. */
    color: string;
    /** How strongly the colour covers the scan inside, 0..1. 0 = no colour (a pure spotlight). */
    colorStrength: number;
    /** Dim the rest of the scene while this region is on. Absent = leave the rest alone. */
    dimOutside?: RegionDim;
    /** On when the scene opens. Default true. A region with triggers starts off and follows them. */
    startsOn?: boolean;
    /** Listed in the visitor Layers menu, where visitors switch it on and off. Default false. */
    inLayersMenu?: boolean;
    /** Layers menu: heading it is listed under (a sub-menu). */
    section?: string;
    /** Layers menu: one-line note under its name. */
    description?: string;
    /** Layers menu: show the total area of its outlines next to its name. */
    showArea?: boolean;
    /** Automatic switches: the same kinds as overlay triggers. A hotspot click toggles it. */
    triggers?: SceneTrigger[];
}

/** The visitor Layers menu (shown while at least one region is in it). */
export interface RegionsMenuConfig {
    /** Show the Layers button and menu. Default true. */
    show?: boolean;
    /** Menu heading and button tooltip. Default "Layers". */
    title?: string;
    /** 'multiple' (default): each layer switches on its own. 'single': picking one shows only it. */
    selectionMode?: 'multiple' | 'single';
    /** Offer an entry that shows every layer at once. Default true. */
    showAllOption?: boolean;
    /** That entry's label. Default "Show all layers". */
    allLabel?: string;
    /** Open the menu when the scene starts. Default false. */
    startOpen?: boolean;
}

export interface RegionsConfig {
    version: 1;
    regions: SceneRegion[];
    menu?: RegionsMenuConfig;
    /** Editor only: draw region outlines and volumes in the viewport. Default true. */
    showBounds?: boolean;
}

export const MAX_REGIONS = 16;
export const MAX_REGION_AREAS = 64;
/**
 * Sphere / box areas across all regions (they count toward MAX_REGION_AREAS too). Each takes a
 * uniform slot (a mat4 and a vec4) in the splat vertex shader, so moving one never recompiles.
 */
export const MAX_REGION_VOLUMES = 8;
/** Splat object areas across all regions (they count toward MAX_REGION_AREAS too): one flag lane each. */
export const MAX_REGION_OBJECT_AREAS = 8;
/** Outline points across all areas (each is a few lines of shader code). */
export const MAX_REGION_OUTLINE_POINTS = 512;
/** Points one outline may have (outline-shape.ts MAX_OUTLINE_POINTS). */
export const MAX_REGION_POINTS_PER_OUTLINE = 48;
/** How long a region takes to fade in or out when it is switched, ms. */
export const REGION_FADE_MS = 250;
export const DEFAULT_REGION_COLOR = '#ffd166';
export const DEFAULT_COLOR_STRENGTH = DEFAULT_ANNOTATION_OPACITY;
/** What a new "dim everything else" starts at (Focus & Highlight's defaults). */
export const DEFAULT_REGION_DIM: RegionDim = { brightness: 0.35, saturation: 0.15 };
export const DEFAULT_MENU_ALL_LABEL = 'Show all layers';
/** Region colours for new regions, in turn (annotation layers' palette). */
export const REGION_PALETTE = DEFAULT_ANNOTATION_COLORS;

const HEX = /^#[0-9a-f]{6}$/i;
const clamp01 = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback);
const hexOr = (v: unknown, fallback: string) => (typeof v === 'string' && HEX.test(v) ? v.toLowerCase() : fallback);

/** The shape fields an area keeps (no feature fields). */
function areaFields(src: Partial<AreaShape> & { shape?: string }, kind: AreaShapeKind): Omit<RegionArea, 'id'> {
    const out: Omit<RegionArea, 'id'> = { shape: kind };
    if (src.position) out.position = { ...src.position };
    if (src.rotation) out.rotation = { ...src.rotation };
    if (src.size) out.size = { ...src.size };
    if (typeof src.feather === 'number') out.feather = src.feather;
    if (Array.isArray(src.points)) out.points = src.points.map((p) => ({ ...p }));
    if (typeof src.depth === 'number') out.depth = src.depth;
    if (src.apply === 'outside' || src.apply === 'inside') out.apply = src.apply as AreaApply;
    if (Array.isArray(src.splatIds)) out.splatIds = [...src.splatIds];
    return out;
}

const focusKind = (shape: unknown): AreaShapeKind => (shape === 'box' || shape === 'outline' || shape === 'splats' ? shape : 'sphere');
const annotationKind = (shape: unknown): AreaShapeKind => (shape === 'sphere' || shape === 'box' || shape === 'splats' ? shape : 'outline');

/** Area id of a converted focus region (one area per focus region). */
const FOCUS_AREA_SUFFIX = ':area';
export const focusAreaId = (regionId: string) => `${regionId}${FOCUS_AREA_SUFFIX}`;

/** Ids of the focus regions keyframe animations target (as `focusRegion`, or already as `region`). */
function animatedRegionIds(animations: readonly AnimationLike[] | undefined | null): Set<string> {
    const ids = new Set<string>();
    if (Array.isArray(animations)) for (const a of animations) if (a && (a.entityType === 'focusRegion' || a.entityType === 'region')) ids.add(a.entityId);
    return ids;
}

/**
 * Scenes saved before Regions: focus regions (`postProcessing.focusRegions`) and annotation layers
 * (`annotationLayers`) as one regions list that looks the same. Annotation layers come first: they
 * were composited first, so focus dimming applied over them, as region dimming does.
 *
 * The old focus effect was one highlight: every focus region that was on combined into one weight
 * (overlaps tinted once, "outside" areas intersected) under one look. So focus regions that are
 * simply on (no triggers, no keyframes on them) and share a colour become ONE region with an area
 * each; separate regions would tint their overlaps twice and union their outsides. Regions with
 * triggers or keyframes stay separate (they switch or move on their own). `animations` are the
 * scene's keyframe animations, to tell which.
 * Returns undefined when there is nothing to convert.
 */
export function regionsFromLegacy(
    focus: FocusConfig | undefined | null,
    annotations: AnnotationLayersConfig | undefined | null,
    animations?: readonly AnimationLike[] | null,
): RegionsConfig | undefined {
    const regions: SceneRegion[] = [];
    let menu: RegionsMenuConfig | undefined;
    let showBounds: boolean | undefined;

    const groups: AnnotationLayerGroup[] = Array.isArray(annotations?.groups) ? annotations!.groups.filter((g) => !!g && typeof g.id === 'string' && !!g.id) : [];
    if (groups.length) {
        const shapes: AnnotationShape[] = Array.isArray(annotations!.shapes) ? annotations!.shapes.filter((s) => !!s && typeof s.id === 'string') : [];
        // Every layer was a layer of the panel, shown or not: a hidden panel becomes a hidden menu
        // (`show: false`) with the layers still in it, so "one layer at a time" keeps applying.
        for (const g of groups) {
            regions.push({
                id: g.id,
                name: typeof g.name === 'string' && g.name.trim() ? g.name : 'Layer',
                enabled: annotations!.enabled === true,
                areas: shapes.filter((s) => s.groupId === g.id).map((s) => ({
                    id: s.id,
                    ...(typeof s.name === 'string' && s.name ? { name: s.name } : {}),
                    ...areaFields(s as unknown as Partial<AreaShape>, annotationKind(s.shape)),
                    ...(s.enabled === false ? { enabled: false } : {}),
                })),
                color: hexOr(g.color, REGION_PALETTE[regions.length % REGION_PALETTE.length]),
                colorStrength: clamp01(g.opacity, DEFAULT_COLOR_STRENGTH),
                startsOn: g.visibleOnLoad !== false,
                inLayersMenu: true,
                ...(typeof g.section === 'string' && g.section ? { section: g.section } : {}),
                ...(typeof g.description === 'string' && g.description ? { description: g.description } : {}),
                ...(g.showArea === true ? { showArea: true } : {}),
                ...(Array.isArray(g.triggers) && g.triggers.length ? { triggers: g.triggers } : {}),
            });
        }
        const p = annotations!.panel;
        menu = {
            ...(p?.show === false ? { show: false } : {}),
            ...(typeof p?.title === 'string' && p.title ? { title: p.title } : {}),
            ...(p?.selectionMode === 'single' ? { selectionMode: 'single' as const } : {}),
            ...(p?.showAllOption === false ? { showAllOption: false } : {}),
            ...(typeof p?.allLabel === 'string' && p.allLabel ? { allLabel: p.allLabel } : {}),
            ...(p?.startOpen === true ? { startOpen: true } : {}),
        };
    }

    const focusRegions: FocusRegion[] = Array.isArray(focus?.regions) ? focus!.regions.filter((r) => !!r && typeof r.id === 'string' && !!r.id) : [];
    if (focusRegions.length) {
        const fallbackTint = hexOr(focus!.tint, DEFAULT_FOCUS_TINT);
        const dimOutside: RegionDim = {
            brightness: clamp01(focus!.outsideBrightness, DEFAULT_REGION_DIM.brightness),
            saturation: clamp01(focus!.outsideSaturation, DEFAULT_REGION_DIM.saturation),
        };
        const colorStrength = clamp01(focus!.tintStrength, 0.2);
        const animated = animatedRegionIds(animations);
        /** The shared region of the focus regions that are simply on, per colour (own colour or the scene's). */
        const shared = new Map<string, SceneRegion>();
        for (const r of focusRegions) {
            const name = typeof r.name === 'string' && r.name.trim() ? r.name : `Highlight ${regions.length + 1}`;
            const enabled = focus!.enabled === true && r.enabled !== false;
            const triggers = Array.isArray(r.triggers) && r.triggers.length ? r.triggers : undefined;
            const area: RegionArea = { id: focusAreaId(r.id), ...areaFields(r as unknown as Partial<AreaShape>, focusKind(r.shape)) };
            const color = hexOr(r.tint, fallbackTint);
            const key = enabled && !triggers && !animated.has(r.id) ? `${HEX.test(r.tint ?? '') ? 'own' : 'scene'}:${color}` : null;
            const host = key ? shared.get(key) : undefined;
            if (host) {
                // Each area keeps its focus region's name, which is also what older viewers get back.
                if (!host.areas[0].name) host.areas[0].name = host.name;
                host.areas.push({ ...area, name });
                continue;
            }
            const region: SceneRegion = {
                id: r.id,
                name,
                enabled,
                areas: [area],
                color,
                colorStrength,
                dimOutside: { ...dimOutside },
                startsOn: true,
                ...(triggers ? { triggers } : {}),
            };
            regions.push(region);
            if (key) shared.set(key, region);
        }
        if (focus!.showBounds === false) showBounds = false;
    }

    if (!regions.length) return undefined;
    return { version: 1, regions, ...(menu && Object.keys(menu).length ? { menu } : {}), ...(showBounds === false ? { showBounds: false } : {}) };
}

/**
 * The regions as focus regions and annotation layers, for viewers that predate Regions (a scene
 * pinned to an older viewer version, an older self-hosted package). Best effort, written next to
 * `regions` on save and never read back by a viewer that knows Regions:
 * - regions that dim the rest become focus regions (at most MAX_FOCUS_REGIONS areas). Older
 *   viewers have one outside look for all of them: the strongest dimming, the first colour strength;
 * - the other regions become annotation layers, one layer per region.
 */
export function legacyFromRegions(config: RegionsConfig | undefined | null): { focusRegions?: FocusConfig; annotationLayers?: AnnotationLayersConfig } {
    const regions = Array.isArray(config?.regions) ? config!.regions : [];
    const spot = regions.filter((r) => !!r.dimOutside);
    const layers = regions.filter((r) => !r.dimOutside);
    const out: { focusRegions?: FocusConfig; annotationLayers?: AnnotationLayersConfig } = {};
    if (spot.length) {
        const focusRegions: FocusRegion[] = [];
        const used = new Set<string>();
        for (const r of spot) {
            // Older viewers have no visitor switch for highlights: one that starts off without
            // triggers (e.g. visitors switch it in the Layers menu) stays off there.
            const enabled = r.enabled && (r.startsOn !== false || !!r.triggers?.length);
            for (const a of r.areas) {
                if (focusRegions.length >= MAX_FOCUS_REGIONS) break;
                if (a.enabled === false) continue;
                const { id: _id, name: _name, enabled: _enabled, ...shape } = a;
                // An area converted from a focus region (`<id>:area`) gets that region's id back.
                const formerId = a.id.endsWith(FOCUS_AREA_SUFFIX) ? a.id.slice(0, -FOCUS_AREA_SUFFIX.length) : null;
                const id = formerId && !used.has(formerId) ? formerId : `${r.id}:${a.id}`;
                used.add(id);
                focusRegions.push({
                    ...(shape as unknown as FocusRegion),
                    id,
                    name: r.areas.length === 1 ? r.name : formerId && a.name ? a.name : `${r.name} (${a.name || a.id})`,
                    enabled,
                    tint: r.color,
                    ...(r.triggers?.length ? { triggers: r.triggers } : {}),
                });
            }
        }
        out.focusRegions = {
            enabled: focusRegions.some((r) => r.enabled),
            regions: focusRegions,
            tint: spot[0].color,
            tintStrength: spot[0].colorStrength,
            outsideBrightness: Math.min(...spot.map((r) => r.dimOutside!.brightness)),
            outsideSaturation: Math.min(...spot.map((r) => r.dimOutside!.saturation)),
            ...(config?.showBounds === false ? { showBounds: false } : {}),
        };
    }
    if (layers.length) {
        const m = config?.menu;
        out.annotationLayers = {
            enabled: layers.some((r) => r.enabled),
            groups: layers.map((r) => ({
                id: r.id,
                name: r.name,
                color: r.color,
                opacity: r.colorStrength,
                visibleOnLoad: r.startsOn !== false,
                ...(r.section ? { section: r.section } : {}),
                ...(r.description ? { description: r.description } : {}),
                ...(r.showArea ? { showArea: true } : {}),
                ...(r.triggers?.length ? { triggers: r.triggers } : {}),
            })),
            // Annotation layers have no on / off of their own: a switched-off region's shapes are off.
            shapes: layers.flatMap((r) => r.areas.map((a) => {
                const { id, name, enabled, ...shape } = a;
                return { ...(shape as unknown as Omit<AnnotationShape, 'id' | 'groupId'>), id, groupId: r.id, points: Array.isArray(a.points) ? a.points : [], ...(name ? { name } : {}), ...(enabled === false || !r.enabled ? { enabled: false } : {}) } as AnnotationShape;
            })),
            panel: {
                show: m?.show !== false && layers.some((r) => r.inLayersMenu),
                ...(m?.title ? { title: m.title } : {}),
                ...(m?.selectionMode === 'single' ? { selectionMode: 'single' as const } : {}),
                ...(m?.showAllOption === false ? { showAllOption: false } : {}),
                ...(m?.allLabel ? { allLabel: m.allLabel } : {}),
                ...(m?.startOpen ? { startOpen: true } : {}),
            },
        };
    }
    return out;
}

/** A keyframe animation as saved (types EntityAnimationConfig), the fields this migration reads. */
interface AnimationLike {
    id: string;
    entityType: string;
    entityId: string;
    tracks: Array<{ property: string; keyframes: unknown[] }>;
    /** Set on the extra copies one legacy animation becomes (migrateRegionAnimations): its id. */
    sourceId?: string;
}

const FOCUS_REGION_PROPS: Record<string, string> = {
    'region.tint.r': 'region.color.r',
    'region.tint.g': 'region.color.g',
    'region.tint.b': 'region.color.b',
};
const ANNOTATION_PROPS: Record<string, string> = {
    'annotation.color.r': 'region.color.r',
    'annotation.color.g': 'region.color.g',
    'annotation.color.b': 'region.color.b',
    'annotation.opacity': 'region.colorStrength',
};
const FOCUS_LOOK_PROPS: Record<string, string> = {
    'focus.tint.r': 'region.color.r',
    'focus.tint.g': 'region.color.g',
    'focus.tint.b': 'region.color.b',
    'focus.tintStrength': 'region.colorStrength',
    'focus.outsideBrightness': 'region.dimBrightness',
    'focus.outsideSaturation': 'region.dimSaturation',
};

/**
 * Keyframe animations that targeted focus regions, the scene-wide focus look or annotation layers,
 * retargeted at the converted regions (entityType 'region'). Region ids are kept by
 * regionsFromLegacy, so an animation keeps its target. The scene-wide focus look animated every
 * focus region at once: it becomes one animation per region converted from them (the colour tracks
 * only for regions without a colour of their own, as before). The first keeps the animation's id,
 * so scripts and hotspot actions that play it by id still find it; any others get `<id>:<region>`
 * and `sourceId` = that id, and playing the id plays them all. Other animations pass through.
 */
export function migrateRegionAnimations<T extends AnimationLike>(animations: readonly T[] | undefined, focus: FocusConfig | undefined | null): T[] {
    if (!Array.isArray(animations)) return [];
    const focusRegions = Array.isArray(focus?.regions) ? focus!.regions.filter((r) => !!r && typeof r.id === 'string') : [];
    // The regions the focus regions become (shared ones included), as regionsFromLegacy makes them.
    const converted = animations.some((a) => a?.entityType === 'focus') ? regionsFromLegacy(focus, undefined, animations)?.regions ?? [] : [];
    const out: T[] = [];
    const retrack = (a: T, map: Record<string, string>, keep?: (prop: string) => boolean): T['tracks'] =>
        a.tracks.filter((t) => map[t.property] !== undefined && (!keep || keep(t.property))).map((t) => ({ ...t, property: map[t.property] })) as T['tracks'];
    for (const a of animations) {
        if (!a || !Array.isArray(a.tracks)) continue;
        if (a.entityType === 'focusRegion') {
            out.push({ ...a, entityType: 'region', tracks: a.tracks.map((t: T['tracks'][number]) => ({ ...t, property: FOCUS_REGION_PROPS[t.property] ?? t.property })) as T['tracks'] });
        } else if (a.entityType === 'annotationLayer') {
            out.push({ ...a, entityType: 'region', tracks: retrack(a, ANNOTATION_PROPS) });
        } else if (a.entityType === 'focus') {
            let first = true;
            for (const region of converted) {
                const source = focusRegions.find((r) => r.id === region.id);
                const ownColour = typeof source?.tint === 'string' && HEX.test(source.tint);
                const tracks = retrack(a, FOCUS_LOOK_PROPS, (prop) => !(ownColour && prop.startsWith('focus.tint.')));
                if (!tracks.length) continue;
                out.push(first
                    ? { ...a, entityType: 'region', entityId: region.id, tracks }
                    : { ...a, id: `${a.id}:${region.id}`, sourceId: a.id, entityType: 'region', entityId: region.id, tracks });
                first = false;
            }
        } else {
            out.push(a);
        }
    }
    return out;
}

/** A new region's defaults: a highlight (dims the rest) or a layer (colour only, in the Layers menu). */
export function newRegion(kind: 'highlight' | 'layer', index: number, id: string): SceneRegion {
    const color = REGION_PALETTE[index % REGION_PALETTE.length];
    return kind === 'highlight'
        ? { id, name: `Highlight ${index + 1}`, enabled: true, areas: [], color: DEFAULT_REGION_COLOR, colorStrength: 0.2, dimOutside: { ...DEFAULT_REGION_DIM }, startsOn: true }
        : { id, name: `Layer ${index + 1}`, enabled: true, areas: [], color, colorStrength: DEFAULT_COLOR_STRENGTH, startsOn: true, inLayersMenu: true };
}

/**
 * The area keyframes can move: a region whose only (enabled) area is a sphere or box animates its
 * position, rotation, size and edge softness (`region.position.*` ...). Null for other regions.
 */
export function regionAnimatableVolume(region: Pick<SceneRegion, 'areas'> | null | undefined): RegionArea | null {
    const areas = Array.isArray(region?.areas) ? region!.areas.filter((a) => !!a && a.enabled !== false) : [];
    return areas.length === 1 && (areas[0].shape === 'sphere' || areas[0].shape === 'box') ? areas[0] : null;
}

export { hexOr as regionHex, clamp01 as regionUnit };
